<template>
  <div
    data-test="us-input-text"
    :class="['us-input us-input--text', { 'input--error': error }]"
  >
    <div v-if="heading" class="display--d2--semibold input-heading-wrapper">
      <component :is="`h${headingLevel}`">
        {{ heading }}
      </component>
      <slot name="heading" />
    </div>
    <div v-if="description" class="font--p1 input-description-wrapper">
      <p>
        {{ description }}
      </p>
      <slot name="description" />
    </div>

    <label :for="inputId">
      <span v-if="prefix" class="us-input-text__input-prefix">
        {{ prefix }}
      </span>
      <input
        :id="inputId"
        :type="inputType"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="modelValue"
        :minlength="minlength"
        :maxlength="maxlength"
        :required="required"
        class="us-input-text__input"
        @input="$emit('update:modelValue', $event.target.value)"
      />
      <span v-if="suffix" class="us-input-text__input-suffix">
        {{ suffix }}
      </span>
      <div v-if="error" class="error body--p2--regular">
        {{ error }}
      </div>
    </label>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

export default defineComponent({
  name: 'USInputText',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    error: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    inputId: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    heading: {
      type: String,
      default: ''
    },
    headingLevel: {
      type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6>,
      default: 2
    },
    description: {
      type: String,
      default: ''
    },
    inputType: {
      type: String,
      default: 'text'
    },
    minlength: {
      type: String,
      default: '0'
    },
    maxlength: {
      type: String,
      default: '255'
    },
    required: {
      type: Boolean,
      default: false
    },
    suffix: {
      type: String,
      default: ''
    },
    prefix: {
      type: String,
      default: ''
    },
    pattern: {
      type: String,
    }
  },
  emits: ['update:modelValue']
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';
@use '@base/styles/v1.0/scss/foundations/color';

.us-input--text {
  width: 100%;

  &.input--error {
    input[type='text'],
    input[type='number'],
    input[type='password'],
    input[type='email'] {
      border-color: var(--color--semantic--error-2) !important;
    }
  }

  .us-input-text__input-prefix {
    margin-left: 20px;
  }

  label {
    // align-items: center;
    display: flex;
    flex-direction: column;
  }

  .us-input-text__input-suffix {
    margin-right: 20px;
  }

  input[type='text'],
  input[type='number'],
  input[type='password'],
  input[type='email'] {
    -moz-appearance: textfield;
    background-color: var(--color--neutral-a5);
    border: var(--line-weight--light) solid var(--color--neutral-a3);
    border-radius: var(--border-radius--small);
    padding: var(--spacing--7);
    width: 100%;

    &::placeholder {
      font-size: inherit;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
      display: none;
    }

    &:hover,
    &:focus {
      border: var(--line-weight--heavy) solid var(--color--primary-b1);
    }

    &:disabled {
      background-color: var(--color--neutral-a4);
      border-color: transparent !important;

      &:hover,
      &:focus {
        cursor: not-allowed;
      }
    }

    @media (min-width: breakpoint.$breakpoint--tablet) {
      padding: 16px 40px;

      &:hover,
      &:focus {
        border: var(--line-weight--heavy) solid var(--color--primary-b1);
      }
    }
  }

  input[type='number'] {
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .error {
    color: var(--color--semantic--error-2);
    padding-top: var(--spacing--8);
  }
}

.input-heading-wrapper {
  margin-bottom: var(--spacing--9);
}

.input-description-wrapper {
  margin-bottom: var(--spacing--8);
}

.us-input-text__input {
  color: var(--color--neutral-a1);
}
</style>
